// Mac App Store Edition filesystem adapter.
//
// Applies the validated rename plan DIRECTLY to disk via stable filesystem
// handles instead of exporting a script. In the packaged Mac app, handles are
// backed by a native bridge (window.__renameStudioNative, see /app/mac). In the
// browser preview, handles are File System Access handles. No AI credentials
// ever live in the client — AI still routes through the FastAPI backend.

import { planExecution, reverseRows } from "@/lib/rename/execution";

async function moveHandle(handle, newName) {
  if (handle && typeof handle.move === "function") {
    await handle.move(newName);
    return;
  }
  throw new Error(
    "Direct rename needs the native Mac app (or a Chromium browser that supports the File System Access move() API)."
  );
}

// Best-effort check that sources still exist right before executing, so a stale
// plan (folder changed after import) is caught instead of blindly executed.
async function verifySources(steps) {
  for (const s of steps) {
    if (s.handle?.exists && !(await s.handle.exists())) {
      return `"${s.oldBasename}" is no longer where it was. Please reload the folder.`;
    }
  }
  return null;
}

// Execute ordered primitive ops. Maintains a log; on any failure rolls back
// every completed op in reverse so the filesystem is never left ambiguous.
async function executeOps(ops, itemTotal, onProgress) {
  const log = [];
  const undoName = new Map(); // handle -> name to move back to during rollback
  let itemsDone = 0;
  for (const op of ops) {
    const before = undoName.get(op.handle) ?? op.__from;
    try {
      await moveHandle(op.handle, op.to);
      log.push({ handle: op.handle, back: before });
      undoName.set(op.handle, op.to);
      if (!op.isTemp) { itemsDone += 1; onProgress?.(itemsDone, itemTotal); }
    } catch (e) {
      for (const d of log.slice().reverse()) {
        try { if (d.back != null) await moveHandle(d.handle, d.back); } catch { /* best effort */ }
      }
      return { ok: false, error: e.message, applied: 0, total: itemTotal, rolledBack: itemsDone };
    }
  }
  return { ok: true, applied: itemsDone, total: itemTotal };
}

// annotate each op with the name it currently has, so rollback can restore it
function annotateFrom(ops, steps) {
  const current = new Map(steps.map((s) => [s.handle, s.oldBasename]));
  return ops.map((op) => {
    const from = current.get(op.handle);
    current.set(op.handle, op.to);
    return { ...op, __from: from };
  });
}

export async function applyPlan(rows, { onProgress } = {}) {
  const { ops, steps } = planExecution(rows);
  if (!ops.length) return { ok: true, applied: 0, total: 0, completed: [] };

  const stale = await verifySources(steps);
  if (stale) return { ok: false, error: stale, applied: 0, total: steps.length, stale: true };

  const res = await executeOps(annotateFrom(ops, steps), steps.length, onProgress);
  if (!res.ok) return res;
  return { ok: true, applied: steps.length, total: steps.length, completed: steps };
}

// Undo a completed apply using the EXACT recorded mapping (never from UI state).
// Re-plans the reverse so swaps/cycles/case-only are undone safely too.
export async function undoPlan(completed, { onProgress } = {}) {
  const rows = reverseRows(completed);
  const { ops, steps } = planExecution(rows);
  if (!ops.length) return { ok: true, undone: 0 };
  const res = await executeOps(annotateFrom(ops, steps), steps.length, onProgress);
  return res.ok ? { ok: true, undone: steps.length } : res;
}

export const macAdapter = {
  id: "mac",
  label: "Mac",
  canImportFolder: true,
  canApplyDirect: true,
  exportPrimary: false,
  importVerb: "Choose Folder",
  applyPlan,
  undoPlan,
};
